import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { completePasswordReset, requestPasswordReset } from "../../Auth/serverSessionApi.ts";
import { useUiI18n } from "../../I18n";
import { useClientDispatch, useClientSelector } from "../../Redux/clientHooks.ts";
import { setUserLanguage, userLanguageSelector } from "../../Redux/clientSlice.ts";
import "./LoginScreen.css";

export const PasswordRecoveryScreen = () => {
	const { pathname } = useLocation();
	const resetting = pathname === "/reset-password";
	const dispatch = useClientDispatch();
	const language = useClientSelector(userLanguageSelector);
	const { t } = useUiI18n();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmation, setConfirmation] = useState("");
	const [busy, setBusy] = useState(false);
	const [done, setDone] = useState(false);
	const [token] = useState(
		() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "",
	);

	useEffect(() => {
		if (!resetting || !window.location.hash) return;
		window.history.replaceState(window.history.state, "", window.location.pathname);
	}, [resetting]);

	const onSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (busy || done) return;
		if (resetting && password !== confirmation) {
			toast.error(t("auth.resetMismatch"));
			return;
		}
		try {
			setBusy(true);
			if (resetting) {
				await completePasswordReset(token, password);
			} else {
				await requestPasswordReset(email);
			}
			setDone(true);
		} catch {
			toast.error(t(resetting ? "auth.resetInvalid" : "auth.resetRequestFailed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<main
			className="login-world"
			style={{ backgroundImage: "url('/images/assets/login/citadel-card-login.webp')" }}
		>
			<div className="login-world__shade" />
			<div className="login-world__layout">
				<header className="login-world__brand">
					<div className="login-world__sigil" aria-hidden="true">
						✦
					</div>
					<p className="login-world__brand-name">{t("app.title")}</p>
				</header>
				<div className="login-world__content">
					<div className="login-world__introduction">
						<p className="login-world__eyebrow">{t("auth.recoveryEyebrow")}</p>
						<h1>{t(resetting ? "auth.resetTitle" : "auth.forgotTitle")}</h1>
						<p>{t(resetting ? "auth.resetIntro" : "auth.forgotIntro")}</p>
					</div>
					<form className="login-world__form login-world__recovery" onSubmit={onSubmit}>
						<div className="login-world__form-top">
							<span>{t("auth.formEyebrow")}</span>
							<label className="login-world__language">
								<span>{t("common.language")}</span>
								<select
									aria-label={t("common.language")}
									value={language}
									onChange={(event) =>
										dispatch(setUserLanguage(event.target.value === "nl" ? "nl" : "en"))
									}
								>
									<option value="nl">{t("language.nl")}</option>
									<option value="en">{t("language.en")}</option>
								</select>
							</label>
						</div>
						{done ? (
							<div className="login-world__recovery-result" role="status">
								<span aria-hidden="true">✧</span>
								<h2>{t(resetting ? "auth.resetDoneTitle" : "auth.forgotDoneTitle")}</h2>
								<p>{t(resetting ? "auth.resetDone" : "auth.forgotDone")}</p>
							</div>
						) : (
							<>
								<h2>{t(resetting ? "auth.resetFormTitle" : "auth.forgotFormTitle")}</h2>
								{resetting && !token ? (
									<p className="login-world__recovery-warning">{t("auth.resetInvalid")}</p>
								) : (
									<>
										<div className="login-world__fields">
											{resetting ? (
												<>
													<label>
														<span>{t("auth.newPassword")}</span>
														<input
															type="password"
															autoComplete="new-password"
															minLength={12}
															maxLength={128}
															required
															value={password}
															onChange={(event) => setPassword(event.target.value)}
														/>
													</label>
													<label>
														<span>{t("auth.confirmPassword")}</span>
														<input
															type="password"
															autoComplete="new-password"
															minLength={12}
															maxLength={128}
															required
															value={confirmation}
															onChange={(event) => setConfirmation(event.target.value)}
														/>
													</label>
												</>
											) : (
												<label>
													<span>{t("auth.email")}</span>
													<input
														type="email"
														autoComplete="email"
														maxLength={254}
														required
														value={email}
														onChange={(event) => setEmail(event.target.value)}
													/>
												</label>
											)}
										</div>
										<button
											className="login-world__submit"
											type="submit"
											disabled={busy || (resetting && password.length < 12)}
										>
											{busy
												? t("auth.resetBusy")
												: t(resetting ? "auth.resetAction" : "auth.forgotAction")}
											<span aria-hidden="true">→</span>
										</button>
									</>
								)}
							</>
						)}
						<Link className="login-world__switch login-world__recovery-back" to="/">
							← {t("auth.backToSignIn")}
						</Link>
					</form>
				</div>
			</div>
		</main>
	);
};
